// Campaign detail — single campaign view with vendor breakdown + clip summary.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, CampaignDetail as CampaignDetailType, CampaignAsset } from '../lib/api';
import { T } from '../theme';

function formatMoney(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusColor(status: string): string {
  switch (status) {
    case 'active':    return T.live;
    case 'draft':     return T.warning;
    case 'running':   return T.live;
    case 'paused':    return T.inkMute;
    case 'completed': return T.inkSoft;
    case 'pending':   return T.warning;
    case 'cancelled': return T.error;
    default:          return T.inkSoft;
  }
}

function laneLabel(lane: string): string {
  switch (lane) {
    case 'creator-seeding': return 'Creator seeding';
    case 'paid-social':     return 'Paid social';
    case 'dsp-editorial':   return 'DSP + editorial';
    default: return lane.replace(/-/g, ' ');
  }
}

export default function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<CampaignDetailType | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [assets, setAssets] = useState<CampaignAsset[]>([]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.campaigns
      .get(id)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, refreshKey]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api.campaigns
      .assets(id)
      .then((res) => {
        if (!cancelled) setAssets(res.assets || []);
      })
      .catch(() => {
        // Asset listing is supplementary to the review — never block the page on it.
        if (!cancelled) setAssets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [id, refreshKey]);

  if (loading) {
    return (
      <div style={detailStyles.empty}>
        <span style={{ color: T.inkMute, fontStyle: 'normal' }}>Loading campaign…</span>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <BackLink />
        <div style={detailStyles.error}>
          <div style={{ color: T.error, fontFamily: T.font.mono, fontStyle: 'normal', marginBottom: 8 }}>
            Failed to load campaign
          </div>
          <div style={{ color: T.inkSoft, fontSize: 13, fontFamily: T.font.mono }}>
            {error.message}
          </div>
        </div>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <BackLink />
        <div style={detailStyles.empty}>
          <span style={{ color: T.inkMute, fontStyle: 'normal' }}>Campaign not found.</span>
        </div>
      </>
    );
  }

  const { campaign, vendors, clip_summary } = data;
  const budgetPct = campaign.budget_total ? (campaign.budget_spent / campaign.budget_total) * 100 : 0;
  const allocatedTotal = vendors.reduce((acc, v) => acc + (v.budget_allocated || 0), 0);
  const spentTotal = vendors.reduce((acc, v) => acc + (v.budget_spent || 0), 0);

  return (
    <>
      <BackLink />

      {/* Hero block */}
      <div style={detailStyles.hero}>
        <div style={detailStyles.heroMeta}>
          <span style={detailStyles.heroBrand}>{campaign.brand_name || '—'}</span>
          <span style={detailStyles.heroSep}>·</span>
          <span style={{ color: statusColor(campaign.status), letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: 11 }}>
            ● {campaign.status}
          </span>
          {campaign.vertical && (
            <>
              <span style={detailStyles.heroSep}>·</span>
              <span style={detailStyles.heroVertical}>{campaign.vertical}</span>
            </>
          )}
        </div>
        <h1 style={detailStyles.heroTitle}>{campaign.title}</h1>
        {campaign.artist && (
          <div style={detailStyles.heroArtist}>
            {campaign.artist}
            {campaign.track ? <span style={{ color: T.accent }}> · {campaign.track}</span> : null}
          </div>
        )}

        {campaign.brief && (
          <p style={detailStyles.heroBrief}>{campaign.brief}</p>
        )}
      </div>

      {['submitted', 'draft', 'awaiting_deposit'].includes(campaign.status) && id && (
        <>
          <ReviewBrief clipsWanted={campaign.clips_wanted} assets={assets} />
          <ApprovePanel
            id={id}
            status={campaign.status}
            cpmRate={campaign.cpm_rate}
            budgetTotal={campaign.budget_total}
            onApproved={() => setRefreshKey((k) => k + 1)}
          />
        </>
      )}

      {/* Stats row */}
      <div style={detailStyles.statRow}>
        <Stat label="Budget total"      value={formatMoney(campaign.budget_total)} />
        <Stat label="Spent"             value={formatMoney(campaign.budget_spent)} sub={`${budgetPct.toFixed(1)}% utilized`} />
        <Stat label="Window"            value={`${formatDate(campaign.starts_at)} → ${formatDate(campaign.ends_at)}`} />
        <Stat label="CPM rate"          value={campaign.cpm_rate ? `$${campaign.cpm_rate.toFixed(2)}` : '—'} sub="per 1K verified" />
      </div>

      {/* Vendor allocation */}
      <Section title="Vendor allocation">
        {vendors.length === 0 ? (
          <div style={detailStyles.empty}>
            <span style={{ color: T.inkMute, fontStyle: 'normal' }}>No vendors allocated yet.</span>
          </div>
        ) : (
          <table style={detailStyles.table}>
            <thead>
              <tr>
                <th style={detailStyles.thLeft}>Vendor</th>
                <th style={detailStyles.thLeft}>Lane</th>
                <th style={detailStyles.thRight}>Allocated</th>
                <th style={detailStyles.thRight}>Spent</th>
                <th style={detailStyles.thLeft}>Status</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id}>
                  <td style={detailStyles.td}>
                    <div style={detailStyles.vendorName}>{v.vendor_slug}</div>
                    {v.notes && <div style={detailStyles.vendorNote}>{v.notes}</div>}
                  </td>
                  <td style={detailStyles.td}>
                    <span style={detailStyles.laneTag}>{laneLabel(v.lane)}</span>
                  </td>
                  <td style={detailStyles.tdRight}>{formatMoney(v.budget_allocated)}</td>
                  <td style={detailStyles.tdRight}>{formatMoney(v.budget_spent)}</td>
                  <td style={detailStyles.td}>
                    <span style={{ color: statusColor(v.status), fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                      ● {v.status}
                    </span>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={2} style={{ ...detailStyles.td, borderBottom: 'none', paddingTop: 14, fontFamily: T.font.mono, fontStyle: 'normal', color: T.inkSoft }}>
                  Total
                </td>
                <td style={{ ...detailStyles.tdRight, borderBottom: 'none', paddingTop: 14, color: T.ink }}>{formatMoney(allocatedTotal)}</td>
                <td style={{ ...detailStyles.tdRight, borderBottom: 'none', paddingTop: 14, color: T.ink }}>{formatMoney(spentTotal)}</td>
                <td style={{ ...detailStyles.td, borderBottom: 'none', paddingTop: 14 }}></td>
              </tr>
            </tbody>
          </table>
        )}
      </Section>

      {/* Clip summary */}
      <Section title="Clip pipeline">
        <div style={detailStyles.clipsRow}>
          <Stat label="Total" value={String(clip_summary?.total ?? 0)} />
          <Stat label="In progress" value={String(clip_summary?.claimed ?? 0)} sub="not submitted yet" />
          <Stat label="Under review" value={String(clip_summary?.submitted ?? 0)} sub="awaiting your review" />
          <Stat label="Verified" value={String(clip_summary?.verified ?? 0)} sub={`${(clip_summary?.total_views ?? 0).toLocaleString()} views`} />
          <Stat label="Earnings paid" value={formatMoney(clip_summary?.total_earnings_cents ?? 0)} />
        </div>
      </Section>

      {/* Performance placeholder */}
      <Section title="Performance">
        <div style={detailStyles.scaffold}>
          <div style={detailStyles.scaffoldLabel}>scaffold</div>
          <div style={detailStyles.scaffoldBody}>
            Performance ingestion (Spotify · Apple · TikTok · Meta · YouTube) lands in
            <em style={{ fontFamily: T.font.mono, fontStyle: 'normal', color: T.accent }}> Phase 5</em>.
            Until then, vendor-reported numbers and verified clip views are the active signal.
          </div>
        </div>
      </Section>
    </>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────
function BackLink() {
  return (
    <Link to="/operator/campaigns" style={detailStyles.backLink}>
      <span style={{ marginRight: 8 }}>←</span> All campaigns
    </Link>
  );
}

// The brief the operator reviews before approving: how many clips the brand
// wants, plus the assets they uploaded (audio, video, reference docs). Shown
// directly above the ApprovePanel so the operator sees the full ask + files,
// then agrees the CPM and approves.
function ReviewBrief({
  clipsWanted,
  assets,
}: {
  clipsWanted: number | null | undefined;
  assets: CampaignAsset[];
}) {
  return (
    <div
      style={{
        margin: '20px 0 0',
        padding: 24,
        background: T.bgCard,
        border: `1px solid ${T.rule}`,
        borderRadius: 14,
      }}
    >
      <div style={{ fontFamily: T.font.mono, fontStyle: 'normal', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.inkMute, marginBottom: 14 }}>
        Brief to review
      </div>

      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: assets.length || clipsWanted ? 18 : 0 }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.inkSoft, marginBottom: 6 }}>
            Clips wanted
          </div>
          <div style={{ fontFamily: T.font.display, fontSize: 24, color: clipsWanted ? T.accent : T.inkMute, lineHeight: 1 }}>
            {clipsWanted ? clipsWanted.toLocaleString() : '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.inkSoft, marginBottom: 6 }}>
            Uploaded assets
          </div>
          <div style={{ fontFamily: T.font.display, fontSize: 24, color: assets.length ? T.accent : T.inkMute, lineHeight: 1 }}>
            {assets.length}
          </div>
        </div>
      </div>

      {assets.length === 0 ? (
        <div style={{ fontSize: 13, color: T.inkMute, fontFamily: T.font.mono, fontStyle: 'normal' }}>
          No files uploaded with this brief.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {assets.map((a) => (
            <li
              key={a.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 14,
                padding: '10px 14px',
                background: T.bg,
                border: `1px solid ${T.rule}`,
                borderRadius: 8,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.filename}
                </div>
                <div style={{ fontFamily: T.font.mono, fontStyle: 'normal', fontSize: 11, color: T.inkSoft, marginTop: 2 }}>
                  {a.kind} · {formatBytes(a.size_bytes)}
                  {a.content_type ? ` · ${a.content_type}` : ''}
                </div>
              </div>
              {a.url ? (
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    flexShrink: 0,
                    fontFamily: T.font.mono,
                    fontStyle: 'normal',
                    fontSize: 11,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: T.accent,
                    textDecoration: 'none',
                    border: `1px solid ${T.rule}`,
                    borderRadius: 6,
                    padding: '6px 12px',
                  }}
                >
                  Open
                </a>
              ) : (
                <span style={{ flexShrink: 0, fontSize: 11, color: T.inkMute, fontFamily: T.font.mono, fontStyle: 'normal' }}>
                  no link
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Operator approval + invoice. Shown while a campaign is pre-payment. Lets the
// operator agree/adjust the CPM, then issues the deposit invoice (charges the
// brand's card on file if present, else emails them a pay link).
function ApprovePanel({
  id,
  status,
  cpmRate,
  budgetTotal,
  onApproved,
}: {
  id: string;
  status: string;
  cpmRate: number | null | undefined;
  budgetTotal: number;
  onApproved: () => void;
}) {
  const [cpm, setCpm] = useState<string>(cpmRate ? String(cpmRate) : '');
  const [budget, setBudget] = useState<string>(budgetTotal ? String(Math.round(budgetTotal / 100)) : '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const budgetCents = Math.round((parseFloat(budget) || 0) * 100);
  const depositCents = Math.floor(budgetCents / 2);

  async function approve() {
    setBusy(true);
    setErr(null);
    try {
      const body: { cpm_rate?: number; budget_total?: number } = {};
      const cpmNum = parseFloat(cpm);
      if (cpmNum > 0) body.cpm_rate = cpmNum;
      if (budgetCents > 0) body.budget_total = budgetCents;
      const res = await api.campaigns.approve(id, body);
      setResult(
        res.method === 'charged'
          ? `Approved. Deposit of $${(res.deposit_due_cents / 100).toLocaleString()} charged to the card on file.`
          : `Approved. Invoice for $${(res.deposit_due_cents / 100).toLocaleString()} emailed to the brand.`
      );
      setTimeout(onApproved, 1400);
    } catch (e) {
      const msg = String(e);
      if (msg.includes('brand_has_no_email')) setErr('This brand has no email on file, so an invoice cannot be sent.');
      else if (msg.includes('already_paid')) setErr('The deposit was already paid.');
      else setErr('Could not approve. Try again, or check the campaign state.');
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        margin: '20px 0 28px',
        padding: 24,
        background: T.bgCard,
        border: `1px solid ${T.rule}`,
        borderRadius: 14,
        borderLeft: `3px solid ${T.accent}`,
      }}
    >
      <div style={{ fontFamily: T.font.mono, fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.inkMute, marginBottom: 6 }}>
        {status === 'submitted' ? 'Submitted for approval' : status === 'awaiting_deposit' ? 'Awaiting deposit' : 'Pending approval'}
      </div>
      <div style={{ fontSize: 18, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Approve & issue invoice</div>
      <p style={{ fontSize: 13.5, color: T.inkSoft, margin: '0 0 18px' }}>
        Agree the CPM, then issue the deposit. The brand's saved card is charged if they have one, otherwise they get a pay link by email.
      </p>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: T.inkSoft }}>
          CPM rate ($ per 1K)
          <input
            type="number"
            step="0.01"
            value={cpm}
            onChange={(e) => setCpm(e.target.value)}
            style={fieldStyle}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: T.inkSoft }}>
          Budget ($)
          <input
            type="number"
            step="1"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            style={fieldStyle}
          />
        </label>
        <div style={{ fontSize: 13, color: T.inkSoft, paddingBottom: 10 }}>
          Deposit (50%): <b style={{ color: T.ink }}>${(depositCents / 100).toLocaleString()}</b>
        </div>
        <button
          type="button"
          onClick={approve}
          disabled={busy || !(budgetCents > 0) || !!result}
          style={{
            padding: '11px 20px',
            background: T.ink,
            color: T.bg,
            border: 'none',
            borderRadius: 9,
            fontSize: 14,
            fontWeight: 600,
            cursor: busy || result ? 'default' : 'pointer',
            opacity: busy || !(budgetCents > 0) || result ? 0.6 : 1,
          }}
        >
          {busy ? 'Issuing…' : result ? 'Approved' : 'Approve & issue invoice'}
        </button>
      </div>

      {result && <p style={{ marginTop: 14, fontSize: 13.5, color: T.live }}>{result}</p>}
      {err && <p style={{ marginTop: 14, fontSize: 13.5, color: T.error }}>{err}</p>}
    </div>
  );
}

const fieldStyle: React.CSSProperties = {
  width: 120,
  padding: '9px 11px',
  background: T.bg,
  color: T.ink,
  border: `1px solid ${T.rule}`,
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  outline: 'none',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={detailStyles.section}>
      <div style={detailStyles.sectionLabel}>{title}</div>
      {children}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={detailStyles.stat}>
      <div style={detailStyles.statValue}>{value}</div>
      <div style={detailStyles.statLabel}>{label}</div>
      {sub && <div style={detailStyles.statSub}>{sub}</div>}
    </div>
  );
}

const detailStyles: Record<string, React.CSSProperties> = {
  empty: {
    padding: 64,
    textAlign: 'center' as const,
    border: `1px solid ${T.rule}`,
    fontSize: 14,
  },
  error: {
    padding: 32,
    border: `1px solid ${T.error}`,
    background: T.errorSoft,
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 13,
    color: T.inkSoft,
    letterSpacing: '0.06em',
    textDecoration: 'none',
    marginBottom: 32,
    padding: '6px 0',
    borderBottom: `1px solid transparent`,
    transition: 'color 0.2s, border-color 0.2s',
  },
  hero: {
    paddingBottom: 32,
    borderBottom: `1px solid ${T.rule}`,
    marginBottom: 32,
  },
  heroMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
    flexWrap: 'wrap' as const,
  },
  heroBrand: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 14,
    color: T.inkSoft,
    letterSpacing: '0.04em',
  },
  heroSep: {
    color: T.inkMute,
  },
  heroVertical: {
    fontSize: 11,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    color: T.accent,
    fontFamily: T.font.mono,
    fontStyle: 'normal',
  },
  heroTitle: {
    fontFamily: T.font.display,
    fontWeight: 400,
    fontSize: 44,
    color: T.ink,
    letterSpacing: '0.012em',
    lineHeight: 1.1,
    marginBottom: 8,
  },
  heroArtist: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 18,
    color: T.ink,
    marginBottom: 18,
  },
  heroBrief: {
    fontSize: 15,
    color: T.inkSoft,
    lineHeight: 1.7,
    maxWidth: 720,
    margin: 0,
  },
  statRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 18,
    marginBottom: 40,
  },
  clipsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 18,
  },
  stat: {
    padding: '20px 18px',
    border: `1px solid ${T.rule}`,
  },
  statValue: {
    fontFamily: T.font.display,
    fontSize: 24,
    color: T.accent,
    letterSpacing: '0.02em',
    marginBottom: 8,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: 10,
    letterSpacing: '0.18em',
    textTransform: 'uppercase' as const,
    color: T.inkSoft,
  },
  statSub: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    color: T.inkMute,
    marginTop: 4,
  },
  section: {
    marginBottom: 48,
  },
  sectionLabel: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase' as const,
    color: T.accent,
    marginBottom: 18,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: 14,
  },
  thLeft: {
    textAlign: 'left' as const,
    padding: '12px 14px 14px 0',
    borderBottom: `1px solid ${T.rule}`,
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    color: T.inkSoft,
    fontWeight: 500,
  },
  thRight: {
    textAlign: 'right' as const,
    padding: '12px 0 14px 14px',
    borderBottom: `1px solid ${T.rule}`,
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    color: T.inkSoft,
    fontWeight: 500,
  },
  td: {
    padding: '14px 14px 14px 0',
    borderBottom: `1px solid ${T.rule}`,
    color: T.ink,
    fontSize: 14,
  },
  tdRight: {
    padding: '14px 0 14px 14px',
    borderBottom: `1px solid ${T.rule}`,
    color: T.ink,
    fontSize: 14,
    textAlign: 'right' as const,
    fontFamily: T.font.mono,
  },
  vendorName: {
    fontFamily: T.font.display,
    fontSize: 16,
    color: T.ink,
    letterSpacing: '0.04em',
    marginBottom: 4,
  },
  vendorNote: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 12,
    color: T.inkSoft,
    lineHeight: 1.4,
  },
  laneTag: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 12,
    color: T.accent,
    letterSpacing: '0.04em',
  },
  scaffold: {
    border: `1px solid ${T.rule}`,
    padding: '20px 24px',
    background: T.accentSoft,
  },
  scaffoldLabel: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase' as const,
    color: T.accent,
    marginBottom: 6,
  },
  scaffoldBody: {
    fontSize: 14,
    color: T.inkSoft,
    lineHeight: 1.65,
  },
};
